/**
 * NEEDS YOU — the list behind the pill. Chip, field test, 2026-08-18.
 *
 *   *"Most of Chip's pictures would not sync. An error at the top of the app
 *    said 'X items need you', but it gave no clarification about what those
 *    items were, how to fix the issue, or what to do. That flag at the top is
 *    causing confusion."*
 *
 * ── What this screen is ─────────────────────────────────────────────────────
 * Every parked outbox row on this phone, grouped by survey, each one saying
 * WHICH (the shot, the question, the survey itself, in the survey's and the
 * section's own names), WHY (the engine's sentence, verbatim — the same one the
 * photo's tile prints), and WHAT TO DO (the one action that can work: try it
 * again, or open the place it lives). Nothing here is red and nothing here says
 * "error": these are decisions waiting, not faults, and every one of them is
 * still saved on this phone (D-015, D-022 §2).
 *
 * ── What it deliberately is not ─────────────────────────────────────────────
 * It does not DELETE. Deleting a photo belongs under the picture, where the
 * surveyor can see which one it is; this screen sends them there. And it does
 * not compose a second story: `attentionRows` (state/attention.ts) is a pure
 * join of `SyncStatus.attention` with the documents on this device, and its
 * `reason` is `SyncAttention.reason` untouched — so this list and the tile it
 * points at cannot describe one photo two ways (D-021).
 *
 * Mounted at `#/needs-you`. The pill in the topbar links here in its "needs
 * you" state and nowhere else.
 */

import { useState, type ReactNode } from 'react';

import { Card, Eyebrow, Notice } from '../components';
import { hrefFor } from '../routing';
import { queueDetail } from '../components/syncPillState';
import { attentionRows, attentionSummary, type AttentionRow } from '../state/attention';
import { useServices } from '../state/services';
import { useSurveyList } from '../state/useSurveys';
import { useSyncStatus } from '../state/useSyncStatus';

export function AttentionScreen(): ReactNode {
  const { sync } = useServices();
  const status = useSyncStatus(sync);
  const { records } = useSurveyList();
  /** The row being sent again right now, so its button says so and only once. */
  const [sending, setSending] = useState<string | null>(null);

  const groups = attentionRows(status.attention, records);
  const total = groups.reduce((sum, group) => sum + group.rows.length, 0);

  const retry = async (row: AttentionRow): Promise<void> => {
    setSending(row.itemId);
    try {
      // One request, once — see `SyncEngine.retryBlocked`. If the office refuses
      // it again the row parks again, with the same words, and stays on this
      // list; if it lands, the row leaves the list on its own.
      await sync.retryBlocked(row.itemId);
    } finally {
      setSending(null);
    }
  };

  return (
    <>
      <Eyebrow>Needs you</Eyebrow>
      <Card>
        <p className="q" data-legibility="question" data-testid="attention-summary">
          {attentionSummary(groups)}
        </p>
        {/* Not the word "error", even to deny it: a surveyor scanning a screen
         * reads the nouns, and the D-015 tests forbid the word on every calm
         * surface for exactly that reason. */}
        {total > 0 ? (
          <p className="ruleline" data-legibility="helper">
            Nothing here is a fault — these are decisions waiting. Each one says what happened
            and the one thing that helps: send it again, or open it where it was taken and decide
            there.
          </p>
        ) : null}
      </Card>

      {status.online ? null : (
        <Notice>
          You are offline. Sending anything again will wait until there is a signal — nothing is
          lost in the meantime.
        </Notice>
      )}

      {/*
       * The pill's red state, explained. `lastError` is the last TRANSIENT
       * refusal — the office said "not right now", not "never" — so the queue
       * is still moving on its own and there is no button here: the one thing
       * that helps is already happening. What the surveyor was missing (2026-08-18,
       * "sync error 9 queued") was the sentence, and the fact that it is not a
       * decision of theirs. Printed in the engine's words, verbatim. */}
      {status.lastError !== null ? (
        <div className="card attn" data-testid="attention-last-error">
          <p className="q" data-legibility="question">
            The last upload did not go through
            <span className="q__hint" data-legibility="helper">
              {status.syncing ? 'Trying again now' : 'It will be tried again on its own'}
              {' — '}
              {queueDetail(status)}.
            </span>
          </p>
          <p className="ruleline attn__reason" data-legibility="helper">
            {status.lastError}
          </p>
        </div>
      ) : null}

      {groups.map((group) => (
        <section key={group.surveyId} aria-label={`Needs you · ${group.surveyTitle}`}>
          <Eyebrow>
            {group.surveyTitle} · {String(group.rows.length)}
          </Eyebrow>
          {group.rows.map((row) => (
            <div className="card attn" key={row.itemId} data-testid="attention-row">
              <p className="q" data-legibility="question">
                {row.what}
                <span className="q__hint" data-legibility="helper">
                  {row.sectionTitle}
                </span>
              </p>
              <p className="ruleline attn__reason" data-legibility="helper">
                {row.reason}
              </p>
              <div className="rowbtns">
                {row.canRetry ? (
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={sending === row.itemId}
                    onClick={() => void retry(row)}
                  >
                    {sending === row.itemId ? 'Sending…' : 'Try sending it again'}
                  </button>
                ) : null}
                {row.route ? (
                  <a className="btn btn--ghost" href={hrefFor(row.route)}>
                    {row.kind === 'photo' ? 'Open the photo' : 'Open the survey'}
                  </a>
                ) : null}
              </div>
            </div>
          ))}
        </section>
      ))}

      <div className="navbar">
        <a className="btn btn--ghost" href={hrefFor({ name: 'home' })}>
          My surveys
        </a>
      </div>
    </>
  );
}
